import type { AttentionBus, WorldHighlight } from '../bridge/AttentionBus';
import type { CommandBus } from '../bridge/CommandBus';
import type { InputController } from '../input/InputController';
import type { Simulation } from '../sim/sim';
import type { BuildingType } from '../sim/types';
import { el } from './dom';
import type { Hud } from './hud';

interface TutorialCtx {
  view: Simulation;
  input: InputController;
}

/** One live checkbox in a step: re-evaluated every frame, and it may UN-check
 * if the player breaks something — that is the point of a live checklist. */
interface ChecklistItem {
  label: string;
  done(ctx: TutorialCtx): boolean;
  /** DOM selectors pulsed while this is the first undone item. */
  highlight?: string[];
  /** Map targets pulsed while this is the first undone item. */
  worldHighlight?(ctx: TutorialCtx): WorldHighlight[];
}

interface TutorialStep {
  id: string;
  title: string;
  text: string;
  objective: string;
  /** CSS selectors pulsed while the step is active (re-asserted per frame,
   * so anchors inside rebuilt panels and transient flyouts stay lit). */
  highlight?: string[];
  /** Sub-objectives rendered as live checkboxes; the first undone item owns
   * the highlights. */
  checklist?: ChecklistItem[];
  /** Map targets pulsed for the whole step (checklist items override). */
  worldHighlight?(ctx: TutorialCtx): WorldHighlight[];
  onEnter?(ctx: TutorialCtx): void;
  done(ctx: TutorialCtx): boolean;
  /** Wrap-up step: completed by its Finish button, not a predicate. */
  manual?: boolean;
}

/** First placed building of each type, as attention-bus targets. */
function worldTargets(ctx: TutorialCtx, ...types: BuildingType[]): WorldHighlight[] {
  const out: WorldHighlight[] = [];
  for (const type of types) {
    const building = ctx.view.state.buildings.find((b) => b.type === type);
    if (building) out.push({ kind: 'building', buildingId: building.id });
  }
  return out;
}

function canteenOf(ctx: TutorialCtx): { powered: boolean; powerEff: number; watered: boolean; staffAssigned: number[] } | undefined {
  return ctx.view.state.buildings.find((b) => b.type === 'canteen');
}

/** The foreman walks a new boss through the first season. Non-blocking: the
 * game runs and every control stays live; the card just keeps score. */
const STEPS: TutorialStep[] = [
  {
    id: 'survey',
    title: 'Survey the claim.',
    text: "Truck's gone, boss. It's you, six hands, and whatever's under this dirt. Have a look around before you spend a nickel.",
    objective: 'Move the camera (drag or arrow keys) or zoom (mouse wheel)',
    onEnter(ctx) {
      surveyStart = { x: ctx.input.camera.x, y: ctx.input.camera.y, zoom: ctx.input.camera.zoom };
    },
    done(ctx) {
      const camera = ctx.input.camera;
      if (!surveyStart) return false;
      return (
        Math.abs(camera.x - surveyStart.x) + Math.abs(camera.y - surveyStart.y) >= 200 ||
        camera.zoom !== surveyStart.zoom
      );
    },
  },
  {
    id: 'dig-tool',
    title: 'Take up the dig tool.',
    text: 'See the dark seam glinting in the ground east of the survey stake? That is iron, and iron is wages. Grab the DIG tool.',
    objective: 'Open Mine in the sidebar and pick Dig (or press E)',
    highlight: ['[data-tutorial="cat-mine"]', '[data-item="t:dig"]'],
    done: (ctx) => ctx.input.tool === 'dig',
  },
  {
    id: 'designate',
    title: 'Mark the seam.',
    text: 'Drag a box over the dark tiles east of the stake. The boys do the digging — you do the pointing.',
    objective: 'Designate at least 6 tiles for digging',
    done: (ctx) => ctx.view.state.orders.size >= 6,
  },
  {
    id: 'first-ore',
    title: 'First ore.',
    text: 'There it is — first ore out of the ground. Haulers will walk it to the depot pile by pile.',
    objective: 'Wait for the first ore to be dug',
    done: (ctx) => ctx.view.state.town.oreMinedTotalDt > 0,
  },
  {
    id: 'crew-roles',
    title: 'Picks and backs.',
    text:
      'Watch them work: the orange shirts are MINERS — they swing picks at your marks. The green shirts are ' +
      'HAULERS — they walk the ore to the depot. Every waged hand draws full pay come Monday, digging or ' +
      'standing idle, so keep the split matched to the work. The Crew panel on the left sets it.',
    objective: 'Raise haulers to 3 in the Crew panel (the + next to haulers)',
    highlight: ['[data-tutorial="haulers-plus"]'],
    done: (ctx) => ctx.view.hud().haulersTarget >= 3,
  },
  {
    id: 'speed-up',
    title: 'Time is money.',
    text: 'Watching men shovel is honest work, but you can make the sun move faster. Nobody minds.',
    objective: 'Set game speed to 3x',
    highlight: ['[data-tutorial="speed-3"]'],
    done: (ctx) => ctx.view.hud().speed >= 3,
  },
  {
    id: 'dispatch',
    title: 'Send the truck.',
    text: 'The depot is filling. SELL ORE sends the truck to market — or it rolls out on its own once it holds about 20 tons.',
    objective: 'Dispatch the ore truck (Sell ore)',
    highlight: ['[data-tutorial="sell"]'],
    done: (ctx) => ctx.view.state.economy.truck.state !== 'idle',
  },
  {
    id: 'first-sale',
    title: 'Cash in the box.',
    text: 'Truck is back and the money is counted. Prices drift week to week — a patient boss sells on the upswing.',
    objective: 'Wait for the sale to settle',
    done: (ctx) => ctx.view.firstSaleDone,
  },
  {
    id: 'payday',
    title: 'Monday settles all.',
    text: 'Come Monday the bookkeeper squares wages, rent, and generator fuel. Keep cash for payday — HQ notices red ink.',
    objective: 'Get through your first payday (the books open on Monday)',
    done: (ctx) => ctx.view.week >= 1,
  },
  {
    id: 'canteen',
    title: 'Feed the crew.',
    text: 'Hungry men dig slow and quit fast. Work the checklist — the canteen reads Operating when every box ticks.',
    objective: 'Get a canteen to Operating',
    checklist: [
      {
        label: 'Build a canteen (Town tab)',
        done: (ctx) => canteenOf(ctx) !== undefined,
        highlight: ['[data-tutorial="cat-town"]', '[data-item="b:canteen"]'],
      },
      {
        label: 'Power it — build near the generator, or run poles',
        done: (ctx) => {
          const b = canteenOf(ctx);
          return b !== undefined && b.powered && b.powerEff >= 0.5;
        },
        highlight: ['[data-tutorial="cat-utilities"]', '[data-item="b:powerPole"]'],
        worldHighlight: (ctx) => worldTargets(ctx, 'canteen', 'generator'),
      },
      {
        label: 'Water it — build near the tank, or lay pipe',
        done: (ctx) => {
          const b = canteenOf(ctx);
          return b !== undefined && b.watered;
        },
        highlight: ['[data-tutorial="cat-utilities"]', '[data-item="t:pipe"]'],
        worldHighlight: (ctx) => worldTargets(ctx, 'canteen', 'waterTank'),
      },
      {
        label: 'Staff it — click the canteen, press +',
        done: (ctx) => {
          const b = canteenOf(ctx);
          return b !== undefined && b.staffAssigned.length >= 1;
        },
        highlight: ['[data-tutorial="staff-plus"]'],
        worldHighlight: (ctx) => worldTargets(ctx, 'canteen'),
      },
    ],
    done: (ctx) => ctx.view.state.buildings.some((b) => b.type === 'canteen' && b.functional),
  },
  {
    id: 'bunkhouse',
    title: 'Room for more.',
    text: 'HQ opened hiring after that first shipment. The Monday bus only stops where there is a bed. A bunkhouse sleeps twelve.',
    objective: 'Build a powered bunkhouse and keep hiring OPEN',
    checklist: [
      {
        label: 'Build a bunkhouse (Housing tab)',
        done: (ctx) => ctx.view.state.buildings.some((b) => b.type === 'bunkhouse'),
        highlight: ['[data-tutorial="cat-housing"]', '[data-item="b:bunkhouse"]'],
      },
      {
        label: 'Power it',
        done: (ctx) => ctx.view.state.buildings.some((b) => b.type === 'bunkhouse' && b.functional),
        highlight: ['[data-tutorial="cat-utilities"]', '[data-item="b:powerPole"]'],
        worldHighlight: (ctx) => worldTargets(ctx, 'bunkhouse', 'generator'),
      },
      {
        label: 'Keep hiring OPEN (Crew panel)',
        done: (ctx) => ctx.view.state.workforce.hiringOpen,
        highlight: ['[data-tutorial="hiring-toggle"]'],
      },
    ],
    done: (ctx) =>
      ctx.view.state.buildings.some((b) => b.type === 'bunkhouse' && b.functional) &&
      ctx.view.state.workforce.hiringOpen,
  },
  {
    id: 'crew-grows',
    title: 'The bus comes Monday.',
    text: 'Beds ready, hiring open. Run the clock and watch the bus roll in.',
    objective: 'Wait for new workers to arrive',
    highlight: ['[data-tutorial="speed-8"]'],
    done: (ctx) => ctx.view.state.peeps.length > 6,
  },
  {
    id: 'wrap-up',
    title: 'You know the trade.',
    text: 'Mail has been piling up while we talked — read it, boss. HQ writes for a reason, and around week 3 they will set a quota. Mind the water tank, mind morale, and build something worth staying for.',
    objective: 'You are on your own now. Good luck.',
    highlight: ['[data-tutorial="mail"]'],
    done: () => false,
    manual: true,
  },
];

let surveyStart: { x: number; y: number; zoom: number } | null = null;

export interface TutorialOptions {
  root: HTMLElement;
  view: Simulation;
  bus: CommandBus;
  input: InputController;
  hud: Hud;
  attention: AttentionBus;
  isWeb: boolean;
  /** Desktop only: a real saved town existed before the tutorial started. */
  hadSave: boolean;
  /** Player chose to keep this claim as their town — start autosaving. */
  onAdopt(): void;
}

export class Tutorial {
  private readonly opts: TutorialOptions;
  private readonly card: HTMLElement;
  private index = 0;
  private flashUntil = 0;
  private finished = false;
  private checkEls: HTMLElement[] = [];
  /** Active DOM highlight set — swept only when it changes, so pulses don't
   * flicker as panels rebuild underneath. */
  private lastDomHighlights = '';

  constructor(opts: TutorialOptions) {
    this.opts = opts;
    this.card = el('div', { class: 'tutorial-card' });
    opts.root.append(this.card);
    STEPS[0]?.onEnter?.(this.ctx());
    this.renderCard();
  }

  /** Once per frame from the game loop. */
  check(): void {
    if (this.finished) return;
    const step = STEPS[this.index];
    if (!step) return;
    const now = performance.now();
    const ctx = this.ctx();

    // Drive the checklist and pick the highlight owner: the first undone
    // item, or the step itself when there is no checklist.
    let activeSelectors = step.highlight ?? [];
    let activeWorld = step.worldHighlight?.(ctx) ?? [];
    if (step.checklist) {
      let firstUndone: ChecklistItem | null = null;
      for (let index = 0; index < step.checklist.length; index += 1) {
        const item = step.checklist[index]!;
        const itemDone = item.done(ctx);
        const row = this.checkEls[index];
        if (row) {
          row.classList.toggle('done', itemDone);
          row.classList.toggle('current', !itemDone && firstUndone === null);
        }
        if (!itemDone && firstUndone === null) firstUndone = item;
      }
      if (firstUndone) {
        activeSelectors = firstUndone.highlight ?? [];
        activeWorld = firstUndone.worldHighlight?.(ctx) ?? activeWorld;
      } else {
        activeSelectors = [];
        activeWorld = [];
      }
    }
    const inFlash = this.flashUntil > 0;
    if (inFlash) {
      activeSelectors = [];
      activeWorld = [];
    }

    // Sweep only when the active set changes; re-assert every frame so
    // anchors inside rebuilt panels and transient flyouts stay lit.
    const selectorKey = activeSelectors.join('|');
    if (selectorKey !== this.lastDomHighlights) {
      this.sweepHighlights();
      this.lastDomHighlights = selectorKey;
    }
    for (const selector of activeSelectors) {
      document.querySelector(selector)?.classList.add('tutorial-pulse');
    }
    this.opts.attention.set(activeWorld);
    // Unread mail always pulses the mailbox — letters land silently while the
    // tutorial owns the screen, and silence must not mean invisible.
    if (this.opts.view.hud().unreadLetters > 0) {
      document.querySelector('[data-tutorial="mail"]')?.classList.add('tutorial-pulse');
    }

    if (inFlash && now >= this.flashUntil) {
      this.flashUntil = 0;
      this.advance();
      return;
    }
    if (!inFlash && !step.manual && step.done(ctx)) {
      this.card.classList.add('tutorial-done');
      this.flashUntil = now + 900;
    }
  }

  private ctx(): TutorialCtx {
    return { view: this.opts.view, input: this.opts.input };
  }

  private advance(): void {
    this.card.classList.remove('tutorial-done');
    this.opts.attention.clear();
    this.index += 1;
    const next = STEPS[this.index];
    if (!next) {
      this.exit('completed');
      return;
    }
    // The wrap-up card tells the player to read their mail — from here on the
    // envelopes open themselves again, whether or not Finish is ever clicked.
    if (next.id === 'wrap-up') this.opts.hud.suppressAutoOpenLetters = false;
    next.onEnter?.(this.ctx());
    this.renderCard();
  }

  private renderCard(): void {
    const step = STEPS[this.index];
    if (!step) return;
    this.card.dataset['step'] = step.id;
    this.lastDomHighlights = '';
    const skip = el('button', { class: 'tutorial-skip', onclick: () => this.confirmSkip() }, 'Skip tutorial');
    const finish = step.manual
      ? el('button', { class: 'modal-close', onclick: () => this.exit('completed') }, 'Finish — take the reins')
      : null;
    this.checkEls = [];
    let checklist: HTMLElement | null = null;
    if (step.checklist) {
      checklist = el('div', { class: 'tutorial-checklist' });
      for (const item of step.checklist) {
        const row = el('div', { class: 'tutorial-check' }, item.label);
        this.checkEls.push(row);
        checklist.append(row);
      }
    }
    this.card.replaceChildren(
      skip,
      el('div', { class: 'tutorial-step-chip' }, `STEP ${this.index + 1}/${STEPS.length}`),
      el('div', { class: 'tutorial-title' }, step.title),
      el('div', { class: 'tutorial-text' }, step.text),
      ...(checklist ? [checklist] : []),
      el('div', { class: 'tutorial-objective' }, `▸ ${step.objective}`),
      ...(finish ? [finish] : []),
    );
  }

  private confirmSkip(): void {
    const skip = this.card.querySelector('.tutorial-skip');
    if (!skip) return;
    skip.replaceWith(
      el('button', { class: 'tutorial-skip', onclick: () => this.exit('skipped') }, 'Really skip?'),
    );
  }

  private sweepHighlights(): void {
    for (const node of document.querySelectorAll('.tutorial-pulse')) node.classList.remove('tutorial-pulse');
  }

  /** Tear down and let the player decide what this claim becomes. */
  private exit(reason: 'completed' | 'skipped'): void {
    if (this.finished) return;
    this.finished = true;
    this.sweepHighlights();
    this.opts.attention.clear();
    this.card.remove();
    this.opts.hud.suppressAutoOpenLetters = false;

    const { root, isWeb, hadSave, onAdopt } = this.opts;
    const title = reason === 'completed' ? 'The foreman tips his hat.' : 'Skipping the lesson.';
    const body =
      reason === 'completed'
        ? 'That is everything a boss needs on day one. What becomes of this claim?'
        : 'Suit yourself — the desert teaches its own way. What becomes of this claim?';
    const backdrop = el('div', { class: 'modal-backdrop' });
    const close = (): void => backdrop.remove();
    const buttons: HTMLElement[] = [];
    if (!isWeb && hadSave) {
      buttons.push(
        el(
          'button',
          { class: 'modal-close', onclick: () => { location.href = location.pathname; } },
          'Return to my town',
        ),
        el(
          'button',
          { class: 'modal-close secondary', onclick: () => { onAdopt(); close(); } },
          'Keep this claim (replaces your save)',
        ),
      );
    } else if (!isWeb) {
      buttons.push(
        el('button', { class: 'modal-close', onclick: () => { onAdopt(); close(); } }, 'Keep playing'),
        el(
          'button',
          { class: 'modal-close secondary', onclick: () => { location.href = location.pathname; } },
          'Back to title',
        ),
      );
    } else {
      buttons.push(
        el('button', { class: 'modal-close', onclick: close }, 'Keep playing'),
        el(
          'button',
          { class: 'modal-close secondary', onclick: () => { location.href = location.pathname; } },
          'Back to title',
        ),
      );
    }
    backdrop.append(
      el('div', { class: 'panel modal' }, el('div', { class: 'modal-title' }, title), el('p', {}, body), ...buttons),
    );
    root.append(backdrop);
  }
}
